/**
 * Super Admin — Activity log
 *
 * Who did what, across every workspace.
 *
 * Like the other admin pages this is not a security boundary: it renders for
 * anyone who navigates here, and audit_logs' RLS policy
 * (is_workspace_admin OR is_platform_master) is the real gate. A non-master
 * simply sees their own workspaces' rows; a non-admin sees none.
 *
 * TWO THINGS THE UI OWES THE READER, both about not overstating what is known:
 *
 * A row with no actor is shown as "unknown", not blanked. Blank reads as a
 * rendering gap; "unknown" is the truth — the write had no resolvable actor,
 * and every such row is a real limit of the trail rather than a bug in the page.
 *
 * The end of the list is stated explicitly. Silently running out of rows looks
 * identical to a read that stopped early, and this codebase has had exactly
 * that failure elsewhere — a truncated list nobody could tell was truncated.
 *
 * @module pages/admin/ActivityLog
 */

import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { YDMPageHero } from "@/components/brand";
import { fetchAuditPage, type AuditEntry, type AuditCursor } from "@/services/auditLogService";

/** Every entity type the audit_logs CHECK allows (migration 120 added result_mapping), plus "everything". */
const ENTITY_FILTERS = [
  { value: "", labelKey: "All activity" },
  { value: "platform_account", labelKey: "Ad platform credentials" },
  { value: "user", labelKey: "User accounts" },
  { value: "workspace", labelKey: "Workspaces" },
  { value: "workspace_member", labelKey: "Membership" },
  { value: "workspace_invitation", labelKey: "Invitations" },
  { value: "looker_template", labelKey: "Looker templates" },
  { value: "budget_plan", labelKey: "Budget plans" },
  { value: "cpr_target", labelKey: "Cost per Result targets" },
  { value: "result_mapping", labelKey: "Result definition" },
] as const;

/** DEC3: how a Result-definition row names its parts. English, like the rest of this page. */
const RESULT_NAMES: Record<string, string> = {
  awareness: "Awareness", consideration: "Consideration", conversion: "Conversion",
  impressions: "Impressions", reach: "Reach", clicks: "Clicks", conversions: "Conversions",
};

/** Bangkok, matching every other date this product shows. */
const formatWhen = (iso: string): string =>
  new Date(iso).toLocaleString("en-GB", {
    timeZone: "Asia/Bangkok",
    year: "numeric", month: "short", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });

/**
 * The detail line. `changes` is written by triggers that deliberately carry no
 * credential material, so rendering it whole is safe — but it is JSON, and a
 * person should not have to read JSON to audit an account.
 */
const describe = (entry: AuditEntry): string => {
  // DEC3: a Result-definition change lives in metadata ({objective, from, to}), not in changes (R7).
  if (entry.entityType === "result_mapping") {
    const m = entry.metadata ?? {};
    const name = (v: unknown): string => (typeof v === "string" ? (RESULT_NAMES[v] ?? v) : "?");
    return `${name(m.objective)}: ${name(m.from)} → ${name(m.to)}`;
  }
  const raw = entry.changes ?? {};
  // The membership triggers nest their payload under `new`/`old`
  // ({"new": {"role": "admin", "email": "…"}}), while the newer credential
  // triggers write flat keys. Reading only the top level rendered an empty
  // Detail column for every row written before today — the information was
  // there and the page was dropping it.
  const nested = (raw.new ?? raw.old ?? {}) as Record<string, unknown>;
  const c: Record<string, unknown> = { ...nested, ...raw };
  const parts: string[] = [];
  if (typeof c.role === "string") parts.push(`role ${c.role}`);
  // DEC5 (budget_plan / cpr_target): what was planned or targeted, in which currency.
  if (typeof c.month === "string") parts.push(`month ${c.month}`);
  if (typeof c.objective === "string") parts.push(`objective ${c.objective}`);
  if (typeof c.metric === "string") parts.push(`metric ${c.metric}`);
  if (typeof c.platform === "string") parts.push(c.platform);
  const currency = typeof c.currency === "string" ? ` ${c.currency}` : "";
  if (typeof c.amount === "number" || typeof c.amount === "string") parts.push(`amount ${c.amount}${currency}`);
  if (typeof c.target === "number" || typeof c.target === "string") parts.push(`target ${c.target}${currency}`);
  if (typeof c.effective_from === "string") parts.push(`from ${c.effective_from}`);
  if (typeof c.account_name === "string") parts.push(c.account_name);
  if (typeof c.email === "string") parts.push(c.email);
  if (typeof c.from === "string" && typeof c.to === "string") parts.push(`${c.from} → ${c.to}`);
  if (c.access_token_changed === true) parts.push("access token replaced");
  if (c.refresh_token_changed === true) parts.push("refresh token replaced");
  if (c.secret_removed === true) parts.push("stored secret removed");
  if (Array.isArray(c.config_keys)) parts.push(`secret written (${c.config_keys.join(", ")})`);
  if (typeof c.status_from === "string" && c.status_from !== c.status_to) {
    parts.push(`status ${c.status_from} → ${String(c.status_to)}`);
  }
  // A DEC5 override or reject reason lives in metadata, not changes (a reject has no changes at all).
  const reason = entry.metadata?.reason;
  if (typeof reason === "string" && reason.trim() !== "") parts.push(`reason: ${reason.trim()}`);
  return parts.join(" · ");
};

const ActivityLog: React.FC = () => {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [cursor, setCursor] = useState<AuditCursor | null>(null);
  const [atEnd, setAtEnd] = useState(false);
  const [entityType, setEntityType] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (after: AuditCursor | null, replace: boolean) => {
    if (replace) { setLoading(true); } else { setLoadingMore(true); }
    setError(null);

    const result = await fetchAuditPage({ after, entityType: entityType || null });

    // ServiceResult is not a discriminated union — `data` is optional even when
    // success is true — so success WITHOUT data is treated as a failure rather
    // than silently rendering an empty trail.
    if (!result.success || !result.data) {
      // An error, never an empty list: a trail that failed to load must not be
      // indistinguishable from a trail with nothing in it.
      setError(result.error ?? "Could not load the activity log");
      if (replace) setEntries([]);
    } else {
      const page = result.data;
      setEntries((prev) => (replace ? page.entries : [...prev, ...page.entries]));
      setCursor(page.nextCursor);
      setAtEnd(page.nextCursor === null);
    }

    setLoading(false);
    setLoadingMore(false);
  }, [entityType]);

  // Re-runs when the filter changes, which is why it replaces rather than
  // appends — appending a filtered page onto an unfiltered list would show rows
  // that do not match the filter the reader can see.
  useEffect(() => { void load(null, true); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-ink-900 animate-spin" aria-label="Loading" />
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <YDMPageHero
        eyebrow="Super Admin"
        headline="ACTIVITY LOG"
        // Honest about scope: the RLS policy is
        // `is_workspace_admin(workspace_id) OR is_platform_master()`, so a
        // non-master sees only workspaces they administer. Claiming "every
        // workspace" would tell an admin their partial view was complete —
        // the exact thing an audit page must never do.
        subtitle="Who did what. A platform master sees every workspace; an admin sees the ones they administer."
      />

      <div className="flex justify-end">
        <label className="flex items-center gap-2 text-xs text-neutral-700">
          Show
          <select
            value={entityType}
            onChange={(e) => setEntityType(e.target.value)}
            className="px-3 py-1.5 text-xs rounded-md border border-neutral-300 bg-white"
          >
            {ENTITY_FILTERS.map((f) => (
              <option key={f.value} value={f.value}>{f.labelKey}</option>
            ))}
          </select>
        </label>
      </div>

      {error && (
        <div role="alert" className="bg-red-50 border border-red-200 text-red-800 rounded-md px-4 py-3 text-sm">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg border border-neutral-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-left text-neutral-700">
            <tr>
              <th className="px-4 py-3 font-medium">When</th>
              <th className="px-4 py-3 font-medium">Who</th>
              <th className="px-4 py-3 font-medium">Did what</th>
              <th className="px-4 py-3 font-medium">Where</th>
              <th className="px-4 py-3 font-medium">Detail</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {entries.map((e) => (
              <tr key={e.id}>
                <td className="px-4 py-3 text-neutral-700 whitespace-nowrap tabular-nums">
                  {formatWhen(e.createdAt)}
                </td>
                <td className="px-4 py-3 text-ink-900">
                  {e.actorEmail ?? (
                    // Not blank. Blank reads as a rendering gap; this is a real
                    // property of the row — nobody was recorded.
                    <span className="text-neutral-700 italic">unknown</span>
                  )}
                </td>
                <td className="px-4 py-3 text-ink-900 whitespace-nowrap">
                  {e.action} <span className="text-neutral-700">{e.entityType}</span>
                </td>
                <td className="px-4 py-3 text-neutral-700">
                  {e.workspaceName ?? <span className="text-neutral-700">—</span>}
                  {e.workspaceName && e.workspaceId === null && (
                    // The workspace is gone but the trail survived it, which is
                    // exactly what migration 071 was for. Say so.
                    <span className="ml-1 text-xs text-neutral-700">(deleted)</span>
                  )}
                </td>
                <td className="px-4 py-3 text-neutral-700">{describe(e)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {entries.length === 0 && !error && (
          <p className="px-4 py-8 text-center text-sm text-neutral-700">
            Nothing recorded yet for this filter.
          </p>
        )}
      </div>

      <div className="flex justify-center">
        {cursor && !atEnd ? (
          <button
            type="button"
            onClick={() => void load(cursor, false)}
            disabled={loadingMore}
            className="px-4 py-2 text-xs font-medium rounded-md border border-neutral-300 hover:bg-neutral-50 disabled:opacity-50"
          >
            {loadingMore ? "Loading…" : "Load older"}
          </button>
        ) : (
          entries.length > 0 && (
            // Stated, not implied. A list that just stops looks the same as one
            // that failed to finish loading.
            <p className="text-xs text-neutral-700">End of the record — {entries.length} entries.</p>
          )
        )}
      </div>
    </div>
  );
};

export default ActivityLog;
